import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  GripVertical, 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward,
  Plus,
  Trash2,
  Music,
  Type,
  Sparkles,
  Clock,
  Film,
  Save,
  Download,
  Share2,
  Edit3,
  X,
  Check,
  Volume2,
  Scissors,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import VideoPreviewPlayer from './VideoPreviewPlayer';

const MUSIC_OPTIONS = [
  { id: 'epic', name: 'Epic Orchestral', bpm: 140 },
  { id: 'hype', name: 'High Energy EDM', bpm: 128 },
  { id: 'cinematic', name: 'Cinematic Drama', bpm: 90 },
  { id: 'motivational', name: 'Motivational Pop', bpm: 120 },
  { id: 'trap', name: 'Trap Sports', bpm: 140 },
];

const TRANSITION_OPTIONS = [
  { id: 'cut', name: 'Hard Cut' },
  { id: 'fade', name: 'Fade' },
  { id: 'zoom', name: 'Zoom Transition' },
  { id: 'whip', name: 'Whip Pan' },
  { id: 'glitch', name: 'Glitch Effect' },
];

const TimelineEditor = ({ 
  initialClips = [], 
  duration = 60, 
  style = 'hype',
  title = 'Highlight Reel',
  reelId,
  videoUrl,
  thumbnailUrl,
  onSave,
  onShare 
}) => {
  const [clips, setClips] = useState(initialClips.length > 0 ? initialClips : [
    { id: '1', title: 'Opening Shot', start: 0, end: 5, type: 'intro', transition: 'fade', text: title },
    { id: '2', title: 'Key Play 1', start: 5, end: 15, type: 'highlight', transition: 'zoom', text: '' },
    { id: '3', title: 'Key Play 2', start: 15, end: 25, type: 'highlight', transition: 'whip', text: '' },
    { id: '4', title: 'Best Moment', start: 25, end: 40, type: 'highlight', transition: 'cut', text: '' },
    { id: '5', title: 'Replay', start: 40, end: 50, type: 'slowmo', transition: 'fade', text: '' },
    { id: '6', title: 'Outro', start: 50, end: 60, type: 'outro', transition: 'fade', text: 'Follow for more!' },
  ]);
  
  const [selectedClip, setSelectedClip] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [editingClip, setEditingClip] = useState(null);
  const [music, setMusic] = useState(style === 'hype' ? 'hype' : style === 'cinematic' ? 'cinematic' : 'epic');
  const [draggedClip, setDraggedClip] = useState(null);
  const [showPreview, setShowPreview] = useState(true);  // Default to showing preview

  const totalDuration = Math.max(...clips.map(c => c.end), duration);

  const handleDragStart = (e, clip) => {
    setDraggedClip(clip);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetClip) => {
    e.preventDefault();
    if (!draggedClip || draggedClip.id === targetClip.id) return;
  };

  const handleDrop = (e, targetClip) => {
    e.preventDefault();
    if (!draggedClip || draggedClip.id === targetClip.id) return;

    const newClips = [...clips];
    const dragIndex = newClips.findIndex(c => c.id === draggedClip.id);
    const dropIndex = newClips.findIndex(c => c.id === targetClip.id);

    // Remove dragged clip and insert at new position
    const [removed] = newClips.splice(dragIndex, 1);
    newClips.splice(dropIndex, 0, removed);

    // Recalculate times
    let currentStart = 0;
    newClips.forEach(clip => {
      const clipDuration = clip.end - clip.start;
      clip.start = currentStart;
      clip.end = currentStart + clipDuration;
      currentStart = clip.end;
    });

    setClips(newClips);
    setDraggedClip(null);
    toast.success('Clip reordered');
  };

  const updateClip = (clipId, updates) => {
    setClips(clips.map(c => c.id === clipId ? { ...c, ...updates } : c));
  };

  const deleteClip = (clipId) => {
    if (clips.length <= 2) {
      toast.error('Need at least 2 clips');
      return;
    }
    const newClips = clips.filter(c => c.id !== clipId);
    // Recalculate times
    let currentStart = 0;
    newClips.forEach(clip => {
      const clipDuration = clip.end - clip.start;
      clip.start = currentStart;
      clip.end = currentStart + clipDuration;
      currentStart = clip.end;
    });
    setClips(newClips);
    setSelectedClip(null);
    toast.success('Clip removed');
  };

  const addClip = () => {
    const lastClip = clips[clips.length - 1];
    const newClip = {
      id: String(Date.now()),
      title: 'New Clip',
      start: lastClip.end,
      end: lastClip.end + 10,
      type: 'highlight',
      transition: 'cut',
      text: ''
    };
    setClips([...clips, newClip]);
    toast.success('Clip added');
  };

  const getClipColor = (type) => {
    switch (type) {
      case 'intro': return 'bg-purple-500';
      case 'highlight': return 'bg-rooam-blue';
      case 'slowmo': return 'bg-orange-500';
      case 'outro': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleExport = () => {
    const exportData = {
      title,
      duration: totalDuration,
      music: MUSIC_OPTIONS.find(m => m.id === music)?.name,
      style,
      clips: clips.map(c => ({
        title: c.title,
        startTime: formatTime(c.start),
        endTime: formatTime(c.end),
        duration: `${c.end - c.start}s`,
        type: c.type,
        transition: c.transition,
        textOverlay: c.text || null
      }))
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}_reel.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Reel exported!');
  };

  const handleSave = () => {
    if (onSave) {
      onSave({ clips, music, totalDuration });
    }
    toast.success('Reel saved!');
  };

  const handleShare = () => {
    if (onShare && reelId) {
      onShare(reelId);
    } else {
      const shareUrl = `${window.location.origin}/share/reel/${reelId || 'preview'}`;
      navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied!');
    }
  };

  return (
    <div className="space-y-4">
      {/* Playback Controls */}
      <Card className="bg-rooam-gray-medium border-rooam-gray-light">
        <CardContent className="p-3 sm:p-4">
          {/* Mobile: Stack controls vertically */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center justify-center sm:justify-start gap-2">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-rooam-gray-light" onClick={() => setCurrentTime(0)}>
                <SkipBack className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-10 w-10 text-white hover:bg-rooam-gray-light"
                onClick={() => setIsPlaying(!isPlaying)}
              >
                {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-rooam-gray-light" onClick={() => setCurrentTime(totalDuration)}>
                <SkipForward className="h-4 w-4" />
              </Button>
              <span className="text-white font-mono text-sm ml-2">
                {formatTime(currentTime)} / {formatTime(totalDuration)}
              </span>
            </div>
            {/* Mobile: action buttons grid */}
            <div className="grid grid-cols-5 sm:flex sm:items-center gap-2">
              <Button variant="outline" size="sm" className="h-9 border-rooam-gray-light text-gray-300 text-xs sm:text-sm" onClick={addClip}>
                <Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Add</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className={`h-9 border-rooam-gray-light text-xs sm:text-sm ${showPreview ? 'bg-rooam-blue text-black' : 'text-gray-300'}`}
                onClick={() => setShowPreview(!showPreview)}
                data-testid="preview-toggle-btn"
              >
                {showPreview ? <EyeOff className="h-4 w-4 sm:mr-1" /> : <Eye className="h-4 w-4 sm:mr-1" />}
                <span className="hidden sm:inline">{showPreview ? 'Hide' : 'Preview'}</span>
              </Button>
              <Button variant="outline" size="sm" className="h-9 border-rooam-gray-light text-gray-300 text-xs sm:text-sm" onClick={handleExport}>
                <Download className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Export</span>
              </Button>
              <Button size="sm" className="h-9 bg-rooam-blue text-black text-xs sm:text-sm" onClick={handleSave}>
                <Save className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Save</span>
              </Button>
              <Button size="sm" variant="outline" className="h-9 border-rooam-blue text-rooam-blue text-xs sm:text-sm" onClick={handleShare}>
                <Share2 className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Share</span>
              </Button>
            </div>
          </div>

          {/* Video Preview Player */}
          {showPreview && (
            <div className="mb-4">
              <VideoPreviewPlayer
                clips={clips}
                title={title}
                music={MUSIC_OPTIONS.find(m => m.id === music)?.name || 'Epic Orchestral'}
                totalDuration={totalDuration}
                videoUrl={videoUrl}
                thumbnailUrl={thumbnailUrl}
              />
            </div>
          )}

          {/* Timeline */}
          <div className="relative bg-rooam-black rounded-lg p-2 sm:p-3">
            {/* Time markers */}
            <div className="flex justify-between text-xs text-gray-500 mb-2">
              {[0, 0.5, 1].map((pct) => (
                <span key={pct}>{formatTime(totalDuration * pct)}</span>
              ))}
            </div>
            
            {/* Clips track - taller on mobile for easier touch */}
            <div className="relative h-20 sm:h-16 bg-rooam-gray-light/30 rounded-lg overflow-hidden touch-pan-x">
              {clips.map((clip) => (
                <div
                  key={clip.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, clip)}
                  onDragOver={(e) => handleDragOver(e, clip)}
                  onDrop={(e) => handleDrop(e, clip)}
                  className={`absolute top-1 bottom-1 rounded cursor-move transition-all ${getClipColor(clip.type)} ${
                    selectedClip?.id === clip.id ? 'ring-2 ring-white' : ''
                  } ${draggedClip?.id === clip.id ? 'opacity-50' : ''}`}
                  style={{
                    left: `${(clip.start / totalDuration) * 100}%`,
                    width: `${((clip.end - clip.start) / totalDuration) * 100}%`,
                    minWidth: '32px'
                  }}
                  onClick={() => setSelectedClip(clip)}
                >
                  <div className="flex items-center h-full px-2 gap-1">
                    <GripVertical className="h-3 w-3 text-white/70 flex-shrink-0" />
                    <span className="text-xs text-white truncate font-medium">{clip.title}</span>
                  </div>
                </div>
              ))}
              
              {/* Playhead */}
              <div 
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10 pointer-events-none"
                style={{ left: `${(currentTime / totalDuration) * 100}%` }}
              >
                <div className="absolute -top-1 -left-1.5 w-3 h-3 bg-red-500 rounded-full" />
              </div>
            </div>

            {/* Music track */}
            <div className="mt-2 flex items-center gap-2">
              <Music className="h-4 w-4 text-purple-400" />
              <div className="flex-1 h-6 bg-purple-500/30 rounded flex items-center px-2">
                <span className="text-xs text-purple-300">{MUSIC_OPTIONS.find(m => m.id === music)?.name}</span>
              </div>
              <Select value={music} onValueChange={setMusic}>
                <SelectTrigger className="w-32 h-6 bg-rooam-black border-rooam-gray-light text-white text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                  {MUSIC_OPTIONS.map(m => (
                    <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clip Editor */}
      {selectedClip && (
        <Card className="bg-rooam-gray-medium border-rooam-gray-light">
          <CardHeader className="py-2 sm:py-3 px-3 sm:px-6">
            <CardTitle className="text-white text-sm flex items-center justify-between">
              <span className="flex items-center gap-2 truncate">
                <Scissors className="h-4 w-4 text-rooam-blue flex-shrink-0" />
                <span className="truncate">Edit: {selectedClip.title}</span>
              </span>
              <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={() => setSelectedClip(null)}>
                <X className="h-4 w-4 text-gray-400" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-3 sm:px-6">
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div className="col-span-2 sm:col-span-1">
                <label className="text-xs text-gray-400">Title</label>
                <Input
                  value={selectedClip.title}
                  onChange={(e) => updateClip(selectedClip.id, { title: e.target.value })}
                  className="h-9 bg-rooam-black border-rooam-gray-light text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400">Type</label>
                <Select value={selectedClip.type} onValueChange={(v) => updateClip(selectedClip.id, { type: v })}>
                  <SelectTrigger className="h-9 bg-rooam-black border-rooam-gray-light text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                    <SelectItem value="intro">Intro</SelectItem>
                    <SelectItem value="highlight">Highlight</SelectItem>
                    <SelectItem value="slowmo">Slow Motion</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-400">Transition</label>
                <Select value={selectedClip.transition} onValueChange={(v) => updateClip(selectedClip.id, { transition: v })}>
                  <SelectTrigger className="h-9 bg-rooam-black border-rooam-gray-light text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                    {TRANSITION_OPTIONS.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-400">Duration</label>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={selectedClip.end - selectedClip.start}
                    onChange={(e) => {
                      const newDuration = parseInt(e.target.value) || 5;
                      updateClip(selectedClip.id, { end: selectedClip.start + newDuration });
                    }}
                    className="h-8 bg-rooam-black border-rooam-gray-light text-white text-sm"
                  />
                  <span className="text-xs text-gray-400">sec</span>
                </div>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 col-span-2">
              <label className="text-xs text-gray-400">Text Overlay</label>
              <div className="flex gap-2">
                <Input
                  value={selectedClip.text || ''}
                  onChange={(e) => updateClip(selectedClip.id, { text: e.target.value })}
                  className="h-9 bg-rooam-black border-rooam-gray-light text-white text-sm flex-1"
                  placeholder="Add text overlay..."
                />
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="h-9 px-3"
                  onClick={() => deleteClip(selectedClip.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clips List */}
      <Card className="bg-rooam-gray-medium border-rooam-gray-light">
        <CardHeader className="py-2 sm:py-3 px-3 sm:px-6">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Film className="h-4 w-4 text-rooam-blue" />
            Clips ({clips.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-3 sm:px-6">
          <div className="space-y-2 max-h-48 sm:max-h-64 overflow-y-auto">
            {clips.map((clip, index) => (
              <div 
                key={clip.id}
                className={`flex items-center gap-2 sm:gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                  selectedClip?.id === clip.id ? 'bg-rooam-blue/20 border border-rooam-blue' : 'bg-rooam-black hover:bg-rooam-gray-light/50'
                }`}
                onClick={() => setSelectedClip(clip)}
              >
                <span className="text-xs text-gray-500 w-5">{index + 1}</span>
                <div className={`w-1.5 sm:w-2 h-8 rounded ${getClipColor(clip.type)}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-white truncate">{clip.title}</p>
                  <p className="text-xs text-gray-500">{formatTime(clip.start)} - {formatTime(clip.end)}</p>
                </div>
                <Badge className="text-xs bg-rooam-gray-light text-gray-300 hidden sm:inline-flex">{clip.type}</Badge>
                <Badge className="text-xs bg-purple-500/20 text-purple-400">{clip.transition}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TimelineEditor;
